/* eslint-disable no-use-before-define */
/* eslint-disable camelcase */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input, Button } from 'fpay-ui';
import styled, { css } from 'styled-components';
import { useHistory } from 'react-router';
import { Grid, Hidden } from '@material-ui/core';

import { FormBackground, FormContainer } from '@components/Forms';
import Card from '@components/Card';
import * as Routes from '@constants/routes';
import { useUser, useForm, useAuth, useEmailAge } from '@hooks';
import { Breadcrumbs } from '@navigation';
import { isAllowed } from '@helpers/auth';
import { parsePhoneObject } from '@helpers';
import { formFieldsHaveValue } from '@helpers/validator';
import { regionPhoneCode } from '@constants/regions';
import FormPasswordConfirmation from '@components/Forms/FormPasswordConfirmation';
import { DocumentInput } from 'screen/UserInviteForm/DocumentInput';
import classes from './UserEdit.module.scss';
import { APPROVED } from '../../constants/user';

const ButtonsContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    margin-top: 20px;
    justify-content: space-between;
    button {
      padding: 11.2px 41.55px;
      &:first-child {
        margin-right: 15px;
      }
      &:last-child {
        width: auto;
      }
    }
    ${theme.breakpoint('md')} {
      display: flex;
      justify-content: flex-end;
      button {
        padding: 11.5px 25px;
        &:last-child {
          padding: 11.5px 27.145px;
        }
      }
    }
  `
);

const UserEdit = ({ route }) => {
  const { updateRepresentant } = useAuth();
  const {
    update,
    selected: {
      _id,
      first_name,
      patern_last,
      matern_last,
      doc_number,
      email,
      phone,
      is_legal_agent,
      doc_type,
      status,
    },
  } = useUser({ fetchUsers: false });
  const { t } = useTranslation();
  const history = useHistory();
  const [showFormPasswordConfirmation, setShowFormPasswordConfirmation] = useState(false);
  const [isNewEmail, setIsNewEmail] = useState(false);
  const [isNewPhone, setIsNewPhone] = useState(false);

  const { isEmailAgeInvalid, setIsEmailAgeInvalid, isErrorEmailAge } = useEmailAge();
  const canEditPhone = isAllowed('USERS_UPDATE_PHONES');
  const canEditName = !is_legal_agent;
  const canEditDocNumber = !is_legal_agent;
  const canEditEmail = is_legal_agent;

  const handleCancel = () => {
    history.push(Routes.USERS);
  };

  const initialValues = {
    first_name,
    matern_last,
    patern_last,
    doc_number,
    email,
    phone_number: `${phone[phone.length - 1].region_id}${phone[phone.length - 1].number}`,
    doc_type,
  };
  const { formValues, handleInputChange, errors, isValid } = useForm(initialValues);

  const handleEdit = () => {
    if (isValid()) {
      if (!is_legal_agent) {
        const { phone_number, ...rest } = formValues;
        update({
          ...rest,
          phone: parsePhoneObject(phone_number, phone[phone.length - 1].country_code),
        });
      } else if (isNewEmail) {
        setShowFormPasswordConfirmation(true);
      } else {
        handleSubmitRepresentant();
      }
    }
  };

  const updateRepresentatError = (error) => {
    if (isErrorEmailAge(error)) {
      setShowFormPasswordConfirmation(false);
    }
  };

  const handleSubmitRepresentant = (password = null) => {
    const { phone_number, ...rest } = formValues;
    const representant = {
      ...rest,
      phones: [parsePhoneObject(phone_number, phone[phone.length - 1].country_code)],
    };
    updateRepresentant(
      {
        legalAgentId: _id,
        representant,
        isNewPhone,
        isNewEmail,
        password,
        updateUserSelected: true,
      },
      updateRepresentatError
    );
  };

  const isSubmitDisabled = formFieldsHaveValue([
    formValues?.first_name,
    formValues?.patern_last,
    formValues?.doc_number,
    formValues?.email,
    formValues?.phone_number,
  ]);

  return (
    <>
      <Grid item container xs={12}>
        <Hidden mdDown>
          <Breadcrumbs route={route} />
        </Hidden>
      </Grid>
      <FormBackground>
        <FormContainer classes={classes.FormContainer}>
          {showFormPasswordConfirmation ? (
            <FormPasswordConfirmation
              email={email}
              handleCancel={() => {
                setShowFormPasswordConfirmation(false);
              }}
              handleSubmit={handleSubmitRepresentant}
            />
          ) : (
            <Card title="Editar informacion del perfil" subtitle=" " className={classes.Card}>
              <Input
                id="first_name"
                testId="first-name-testId"
                label={t('editname.name')}
                value={formValues.first_name}
                onChange={handleInputChange}
                error={!!errors.first_name}
                caption={errors.first_name}
                disabled={!canEditName}
              />
              <Input
                id="patern_last"
                testId="patern-last-testId"
                label={t('editname.lastname')}
                value={formValues.patern_last}
                onChange={handleInputChange}
                error={!!errors.patern_last}
                caption={errors.patern_last}
                disabled={!canEditName}
              />
              <Input
                id="matern_last"
                testId="matern-last-testId"
                label={t('editname.mother_lastname')}
                value={formValues.matern_last}
                onChange={handleInputChange}
                error={!!errors.matern_last}
                caption={errors.matern_last}
                disabled={!canEditName}
              />
              <DocumentInput
                errors={errors}
                formValues={formValues}
                handleChange={handleInputChange}
                disabled={!canEditDocNumber}
              />
              <Input
                id="email"
                testId="email-testId"
                label={t('users.detail.email')}
                value={formValues.email}
                onChange={(e) => {
                  setIsNewEmail(true);
                  setIsEmailAgeInvalid(false);
                  handleInputChange(e);
                }}
                error={!!errors.email || isEmailAgeInvalid}
                caption={errors.email}
                disabled={!canEditEmail}
              />
              {phone.length && (
                <Input
                  id="phone_number"
                  testId="phone-testId"
                  label={t('users.detail.phone')}
                  value={formValues.phone_number}
                  onChange={(e) => {
                    setIsNewPhone(true);
                    handleInputChange(e);
                  }}
                  error={!!errors.phone_number}
                  caption={errors.phone_number}
                  countryCode={regionPhoneCode}
                  disabled={!canEditPhone}
                />
              )}
              <ButtonsContainer>
                <Button
                  size="medium"
                  type="outlined"
                  onClick={handleCancel}
                  id={
                    status === APPROVED
                      ? 'cancel-edit-collaborator-invitation-information'
                      : 'cancel-edit-pending-collaborator-invitation-information'
                  }
                >
                  {t('common.buttonCancel')}
                </Button>
                <Button
                  id={
                    status === APPROVED
                      ? 'confirm-edit-collaborator-invitation-information'
                      : 'confirm-edit-pending-collaborator-invitation-information'
                  }
                  size="medium"
                  onClick={handleEdit}
                  disabled={isSubmitDisabled || isEmailAgeInvalid}
                >
                  {t('common.continue')}
                </Button>
              </ButtonsContainer>
            </Card>
          )}
        </FormContainer>
      </FormBackground>
    </>
  );
};

export default UserEdit;
